import React, { useEffect, useRef } from 'react';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import CalendarTodayOutlined from '@mui/icons-material/CalendarTodayOutlined';

export default function QuoteDateInput({ value, onChange, onTabNext, autoFocus = false, error = false }: {
  value: string; onChange: React.ChangeEventHandler<HTMLInputElement>; autoFocus?: boolean; error?: boolean;
  onTabNext?: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const calendarOpen = useRef(false);
  const openCalendar = () => {
    const field = input.current as (HTMLInputElement & { showPicker?: () => void }) | null;
    field?.focus();
    try {
      if (field?.showPicker) {
        field.showPicker();
        calendarOpen.current = true;
      }
    } catch {
      calendarOpen.current = false;
      // Browsers that require a direct user gesture still leave the focused
      // date input ready for typed entry.
    }
  };

  useEffect(() => {
    if (!autoFocus) return;
    const frame = window.requestAnimationFrame(() => {
      input.current?.focus();
      openCalendar();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [autoFocus]);

  return <TextField size="small" type="date"
    name="startDate" value={value}
    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(event);
      if (calendarOpen.current && event.target.value) {
        calendarOpen.current = false;
        // The native picker has no close API. Blurring its focused field
        // dismisses it after committing a date without submitting the row.
        input.current?.blur();
        // Restore the field after dismissing the native popup so the next Tab
        // reaches our Quantity shortcut, rather than re-entering a date segment.
        window.requestAnimationFrame(() => onTabNext ? onTabNext() : input.current?.focus());
      }
    }} onBlur={() => { calendarOpen.current = false; }}
    error={error} inputRef={input}
    inputProps={{ 'aria-label': 'Shift date', 'aria-keyshortcuts': 'Alt+ArrowDown' }}
    onKeyDown={event => {
      if ((event.key === 'Tab' || event.key === 'Enter') && !event.shiftKey && onTabNext) {
        event.preventDefault(); event.stopPropagation(); onTabNext();
        return;
      }
      if (event.altKey && event.key === 'ArrowDown') {
        event.preventDefault(); event.stopPropagation(); openCalendar();
        return;
      }
      // Keyboard edits in the field must not lose focus between date segments.
      calendarOpen.current = false;
    }}
    sx={{ width: 108, '& .MuiInputBase-root': { padding: 0 },
      '& input': { padding: '3px 0', fontSize: '12px', textAlign: 'center' },
      '& input::-webkit-datetime-edit': { padding: 0, textAlign: 'center' },
      '& input::-webkit-calendar-picker-indicator': { display: 'none' } }}
    InputProps={{ endAdornment: <InputAdornment position="end" sx={{ ml: 0 }}>
      <IconButton size="small" tabIndex={-1} aria-label="Open calendar" title="Open calendar (Alt+Down)" onClick={openCalendar} sx={{ p: 0 }}>
        <CalendarTodayOutlined sx={{ fontSize: 16 }} />
      </IconButton>
    </InputAdornment> }} />;
}
